import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import {PalletTransport} from './components/PalletTransport'
import {usePalletCapabilities, usePalletOverview} from '@/api/warehouse'
// src/pages/warehouse/NachschubPage.tsx
// Task 14: Leiter-Dashboard fuer Nachschub/Fillup (Modul ②). Gestaltungssprache
// EXAKT wie LeiterListenPage.tsx (Tab-Buttons, Zeilen-Karten, lead-gated Aktionen,
// useInfiniteQuery-Cursor) — Mirror, keine neuen shadcn-Komponenten.
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/api/auth'
import { useFillupOverview, useCancelFillupRequest, useFillupOverrides } from '@/api/warehouse'
import { Button } from '@/components/ui/button'
import { AuftragAnlegenDialog } from './components/AuftragAnlegenDialog'
import { ZeilenMenge } from './components/ZeilenMenge'
import { AuftragSymbol } from './components/AuftragSymbol'
import { neuerVorgangsspeicher, type Vorgangsspeicher } from '@/lib/fillup-anforderung'
import { Input } from '@/components/ui/input'
import { WarehouseCompanyFilter } from '@/components/warehouse/WarehouseCompanyFilter'
import { warehouseErrorMessage, type WarehouseCompanyFilter as CompanyFilter } from '@/lib/warehouse-company'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Info } from 'lucide-react'

const TABS = [
  // P3 (Welle 1b): Schluessel bleiben technisch, Beschriftung folgt der Rollen-Sprache.
  // Modul 22 B: 'Overrides' war fuer die Nutzer unverstaendlich -> 'Übersteuerungen'
  // (Schluessel + API-Pfad bleiben 'overrides').
  { key: 'picking', label: 'Kommissionier-Liste' },
  { key: 'vormerkungen', label: 'Karton-Vormerkungen' },
  { key: 'overrides', label: 'Übersteuerungen' },
] as const
type TabKey = (typeof TABS)[number]['key']

/** "wartet seit X Tagen"/"wartet seit X Std." — analog zu escalation.daysOpen,
 *  aber feiner (Stunden) fuer frische Vormerkungen unter 1 Tag. */
function formatAge(createdAt: string): string {
  const ms = Date.now() - new Date(createdAt).getTime()
  const days = Math.floor(ms / 86_400_000)
  if (days >= 1) return `wartet seit ${days} Tag${days === 1 ? '' : 'en'}`
  const hours = Math.max(0, Math.floor(ms / 3_600_000))
  return `wartet seit ${hours} Std.`
}

/** Modul 26 C: reiner Anzeige-Filter. Trifft auf Sternnummer ODER Artikelname,
 *  Gross-/Kleinschreibung egal. Kein Server-Aufruf, die Laufreihenfolge bleibt. */
export function passtZurSuche(zeile: Record<string, unknown>, suche: string): boolean {
  const text = suche.trim().toLowerCase()
  if (!text) return true
  const sku = String(zeile['merchant_sku'] ?? '').toLowerCase()
  const name = String(zeile['artikel_name'] ?? '').toLowerCase()
  return sku.includes(text) || name.includes(text)
}

function ZoneSection({ title, items, lead, count, vorgaenge, onCancel, cancelPending }: {
  title: string
  items: Array<Record<string, unknown>>
  lead: boolean
  count: boolean
  vorgaenge: Vorgangsspeicher
  onCancel: (requestDbId: number) => void
  cancelPending: boolean
}) {
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-muted">{title}</h2>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={Number(item['id'])} className="rounded-lg border border-line bg-panel p-3 flex items-start gap-3 flex-wrap">
            <AuftragSymbol art="karton" />
            <div className="flex-1 min-w-[220px] text-sm">
              <div>
                <b><GrundartikelLink code={String(item['merchant_sku'])} /></b>
                {item['artikel_name'] ? <> — {String(item['artikel_name'])}</> : null}
              </div>
              <div className="text-base font-semibold">{String(item['place_barcode'] ?? '—')}</div>
              <div className="text-xs text-muted">
                {/* Modul 26 B: mit Steller gehoert die Zahl dem Steller — zwei Zahlen,
                    die waehrend des Aenderns auseinanderlaufen, verwirren nur. */}
                {!count && <>{String(item['cartons'])} Kartons · </>}
                {item['palette_no'] ? <> · Palette {String(item['palette_no'])}</> : null}
                {' · '}<span className="rounded-full bg-primary-soft text-primary px-2 py-0.5">{item['in_arbeit_durch'] ? `In Arbeit · ${String(item['in_arbeit_durch'])}` : String(item['status'])}</span>
              </div>
            </div>
            {count && (
              <ZeilenMenge
                merchantSku={String(item['merchant_sku'])}
                menge={Number(item['cartons'])}
                requestDbId={Number(item['request_db_id'])}
                vorgaenge={vorgaenge}
              />
            )}
            {lead && (
              <Button size="sm" variant="outline" disabled={cancelPending}
                onClick={() => onCancel(Number(item['request_db_id']))}>
                Stornieren
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function PickingTab({ lead, count, company }: { lead: boolean; count: boolean; company: CompanyFilter }) {
  const [suche, setSuche] = useState('')
  // Ein Speicher fuer die ganze Liste: er ueberlebt das Aus- und Wiedereinblenden
  // einzelner Zeilen durch den Suchfilter (Codex-Review 2026-08-04, Befund 3).
  const vorgaengeRef = useRef(neuerVorgangsspeicher())
  const overview = useFillupOverview(company)
  const cancel = useCancelFillupRequest()

  async function onCancel(requestDbId: number) {
    try {
      await cancel.mutateAsync(requestDbId)
      toast.success('Storniert')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
  }

  if (overview.isError) {
    return (
      <div className="text-sm text-red-600 flex items-center gap-2">
        {warehouseErrorMessage(overview.error)}
        <Button size="sm" variant="outline" onClick={() => void overview.refetch()}>Erneut versuchen</Button>
      </div>
    )
  }
  if (overview.isLoading) return <Skeleton className="h-24 w-full" />

  const progress = overview.data?.progress
  const alleFuss = overview.data?.lists?.fuss ?? []
  const alleStapler = overview.data?.lists?.stapler ?? []
  const fuss = alleFuss.filter((z) => passtZurSuche(z, suche))
  const stapler = alleStapler.filter((z) => passtZurSuche(z, suche))
  const gesamt = alleFuss.length + alleStapler.length
  const gezeigt = fuss.length + stapler.length
  const filtert = suche.trim().length > 0

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-line bg-panel p-3 text-sm flex gap-4 flex-wrap items-center">
        <div>Offen: <b>{progress ? Number(progress.offen) : '—'}</b></div>
        <div>Erledigt heute: <b>{progress ? Number(progress.erledigt_heute) : '—'}</b></div>
      </div>
      {/* Modul 26 C: Lupe. Filtert beide Zonen sofort, ohne Server-Aufruf. */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <span aria-hidden className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted">🔎</span>
          <Input
            aria-label="Zeilen durchsuchen"
            className="pl-8"
            placeholder="Sternnummer oder Artikel suchen"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>
        {filtert && (
          <>
            <span className="text-xs text-muted">{gezeigt} von {gesamt} Zeilen</span>
            <Button size="sm" variant="outline" onClick={() => setSuche('')}>Suche löschen</Button>
          </>
        )}
      </div>

      {gezeigt === 0 && <p className="text-sm text-muted">{filtert ? 'Keine passenden Positionen.' : 'Keine offenen Positionen.'}</p>}
      {fuss.length > 0 && <ZoneSection title="Zone: Fuss" items={fuss} lead={lead} count={count} vorgaenge={vorgaengeRef.current}
        onCancel={(id) => void onCancel(id)} cancelPending={cancel.isPending} />}
      {stapler.length > 0 && <ZoneSection title="Zone: Stapler" items={stapler} lead={lead} count={count} vorgaenge={vorgaengeRef.current}
        onCancel={(id) => void onCancel(id)} cancelPending={cancel.isPending} />}
    </div>
  )
}

function VormerkungenTab({ lead, company }: { lead: boolean; company: CompanyFilter }) {
  const overview = useFillupOverview(company)
  const cancel = useCancelFillupRequest()

  // Storno einer Vormerkung: die Vormerkung-`id` IST die Anforderungs-DB-id
  // (getFillupOverview mappt warehouseFillupRequests.id), also derselbe Storno-Pfad
  // wie in der Picking-Liste (POST /fillup/request/:id/cancel/, Backend-Gate 'lead').
  async function onCancel(requestDbId: number) {
    try {
      await cancel.mutateAsync(requestDbId)
      toast.success('Vormerkung storniert')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
  }

  if (overview.isError) {
    return (
      <div className="text-sm text-red-600 flex items-center gap-2">
        {warehouseErrorMessage(overview.error)}
        <Button size="sm" variant="outline" onClick={() => void overview.refetch()}>Erneut versuchen</Button>
      </div>
    )
  }
  if (overview.isLoading) return <Skeleton className="h-24 w-full" />

  const items = overview.data?.vormerkungen ?? []
  return (
    <ul className="space-y-2">
      {items.map((v) => (
        <li key={Number(v['id'])} className="rounded-lg border border-line bg-panel p-3 flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px] text-sm">
            <div>
              <b><GrundartikelLink code={String(v['merchant_sku'])} /></b>
              {v['artikel_name'] ? <> — {String(v['artikel_name'])}</> : null}
            </div>
            <div className="text-xs text-muted">
              <b className="text-base text-ink">{String(v['cartons'])} Kartons</b> · <span className="rounded-full bg-amber-50 text-amber-900 px-2 py-0.5">Wartet</span> · {formatAge(String(v['created_at']))}
              <Popover><PopoverTrigger asChild><Button variant="ghost" size="icon" aria-label="Quelle der Vormerkung"><Info className="h-4 w-4" /></Button></PopoverTrigger><PopoverContent>Quelle: {String(v['source'])}</PopoverContent></Popover>
            </div>
          </div>
          {lead && (
            <Button size="sm" variant="outline" disabled={cancel.isPending}
              onClick={() => void onCancel(Number(v['id']))}>
              Stornieren
            </Button>
          )}
        </li>
      ))}
      {items.length === 0 && !overview.isLoading && (
        <li className="text-muted text-sm p-3">Keine Karton-Vormerkungen.</li>
      )}
    </ul>
  )
}

function OverridesTab({ company, sinceDays }: { company: CompanyFilter; sinceDays: 7 | 30 }) {
  const overrides = useFillupOverrides(sinceDays, company)

  const rows = useMemo(
    () => overrides.isError ? [] : (overrides.data?.pages ?? []).flatMap((p) => p.items),
    [overrides.data, overrides.isError],
  )

  return (
    <div className="space-y-2">
      {/* Modul 22 B: Ein-Satz-Erklaerung direkt auf der Seite (Muster: Leiter-Listen-
          Legende OI-130 P3) — der Reiter-Name allein erklaert den Inhalt nicht. */}

      {overrides.isError && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          {warehouseErrorMessage(overrides.error)}
          <Button size="sm" variant="outline" onClick={() => void overrides.refetch()}>Erneut versuchen</Button>
        </div>
      )}
      {overrides.isLoading && <Skeleton className="h-24 w-full" />}

      <ul className="space-y-2">
        {rows.map((o) => (
          <li key={Number(o['id'])} className="rounded-lg border border-line bg-panel p-3 text-sm">
            <div>
              <b><GrundartikelLink code={String(o['merchant_sku'])} /></b>
              {o['artikel_name'] ? <> — {String(o['artikel_name'])}</> : null}
              {' · '}Palette {String(o['palette_no'] ?? '—')}
            </div>
            <div className="text-xs text-muted">
              {o['picked_at'] ? new Date(String(o['picked_at'])).toLocaleString('de-DE') : '—'}
              {' · '}Wer: {String(o['picked_by_worker_name'] ?? '—')}
              {' · '}Grund: {String(o['override_reason'] ?? '—')}
            </div>
          </li>
        ))}
        {rows.length === 0 && !overrides.isLoading && !overrides.isError && (
          <li className="text-muted text-sm p-3">Keine Übersteuerungen im gewählten Zeitraum.</li>
        )}
      </ul>

      {overrides.hasNextPage && (
        <div className="flex justify-center pt-1">
          <Button size="sm" variant="outline" disabled={overrides.isFetchingNextPage}
            onClick={() => void overrides.fetchNextPage()}>
            {overrides.isFetchingNextPage ? 'Lade…' : 'Weitere laden'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default function NachschubPage() {
  const { warehouse, user } = useAuth()
  const capability=usePalletCapabilities()
  const [tab, setTab] = useState<TabKey>('picking')
  const [sinceDays, setSinceDays] = useState<7 | 30>(7)
  const [lager, setLager] = useState<1 | 2>(2)
  // Stahlia ist vorgewaehlt; Priostahl oder beide Firmen nur auf Klick.
  const [company, setCompany] = useState<CompanyFilter>('stahlia')
  const companies: CompanyFilter[] = warehouse.lead
    ? company === 'both' ? ['stahlia', 'priostahl'] : [company]
    : ['both']
  // Ohne Leiterrecht gibt es keine Firmenwahl: die Palettenliste bleibt firmenneutral wie bisher.
  const palletCompany: CompanyFilter = warehouse.lead ? company : 'both'
  const dual = warehouse.lead && company === 'both'
  const counts = useFillupOverview(dual ? 'stahlia' : palletCompany)
  const priCounts = useFillupOverview(dual ? 'priostahl' : palletCompany, dual)
  const palletCounts = usePalletOverview(palletCompany, lager, capability.data?.available === true)
  const countData = counts.data?.progress && counts.data.vormerkungen && (!dual || (priCounts.data?.progress && priCounts.data.vormerkungen)) ? {
    offen: counts.data.progress.offen + (dual ? priCounts.data!.progress.offen : 0),
    vormerkungen: counts.data.vormerkungen.length + (dual ? priCounts.data!.vormerkungen.length : 0),
  } : null

  return (
    <div className="p-4 space-y-3 max-w-5xl mx-auto">
      <h1 className="text-xl font-bold">Kommissionieren</h1>
      <WarehouseCompanyFilter value={company} onChange={setCompany} buttons />

      <div className="flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm ${tab === t.key ? 'bg-primary-soft text-primary font-medium' : 'text-muted hover:bg-panel'}`}>
            {t.label}{t.key === 'picking' && countData ? ` (Kartons ${countData.offen}${capability.data?.available && palletCounts.data?.lists ? ` · Paletten ${palletCounts.data.lists.fuss.length + palletCounts.data.lists.stapler.length}` : ''})` : t.key === 'vormerkungen' && countData ? ` (${countData.vormerkungen})` : ''}
          </button>
        ))}
      </div>

      {/* Ein gemeinsamer Einstieg; das bestehende Schreibrecht bleibt count. */}
      {tab === 'picking' && warehouse.count && <AuftragAnlegenDialog key={user?.id} company={palletCompany} />}
      {tab === 'overrides' && <div className="flex items-center gap-2">
        <Popover><PopoverTrigger asChild><Button variant="ghost" size="icon" aria-label="Information zu Übersteuerungen"><Info className="h-4 w-4" /></Button></PopoverTrigger><PopoverContent>Entnahmen von einer anderen als der vorgeschlagenen Palette, mit Grund, Mitarbeiter und Zeitpunkt.</PopoverContent></Popover>
        {([7, 30] as const).map(d => <Button key={d} size="sm" variant={sinceDays === d ? 'default' : 'outline'} onClick={() => setSinceDays(d)}>{d} Tage</Button>)}
      </div>}
      {/* Paletten und Kartons auf einer Seite, je Art gebuendelt: die wenigen Paletten
          oben, damit sie unter der langen Kartonliste nicht verschwinden. */}
      {tab === 'picking' && capability.data?.available && (
        <section aria-label="Palettenaufträge" className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2"><AuftragSymbol art="palette" />Palettenaufträge</h2>
          <PalletTransport key={palletCompany} company={palletCompany} enabled={capability.data.enabled} lager={lager} onLagerChange={setLager} />
        </section>
      )}
      <section aria-label={tab === 'picking' ? 'Kartonaufträge' : undefined} className="space-y-3">
        {tab === 'picking' && <h2 className="text-lg font-semibold flex items-center gap-2"><AuftragSymbol art="karton" />Kartonaufträge</h2>}
        {companies.map(scope => (
          <section key={scope} aria-label={scope === 'priostahl' ? 'Priostahl' : 'Stahlia'} className="space-y-3">
            {warehouse.lead && company === 'both' && <h3 className="text-base font-semibold">{scope === 'priostahl' ? 'Priostahl' : 'Stahlia'}</h3>}
            {tab === 'picking' && <PickingTab lead={warehouse.lead} count={warehouse.count} company={scope} />}
            {tab === 'vormerkungen' && <VormerkungenTab lead={warehouse.lead} company={scope} />}
            {tab === 'overrides' && <OverridesTab company={scope} sinceDays={sinceDays} />}
          </section>
        ))}
      </section>
    </div>
  )
}
